// Core pakages
import { Injectable } from "@angular/core";

// Third party packages
import { TranslateService } from "@ngx-translate/core";

// Custom packages
import { environment } from "../../../environments/environment";
import countriesData from "../mocks/countries.json";

@Injectable({
  providedIn: "root"
})
export class ConfigService {
  // Developer data
  developerName = "SITI srl";
  developerFullName = "Società Italiana Tecnologia e Informatica";
  developerSupportEmail = "assistenza@sitisrl.it";
  developerWebsite = "www.sitisrl.it";
  developerPhone = "+39 039 2271101";

  // App owner data
  ownerName = "Studio Associato Gamma S.r.l.";
  ownerNicename = "Archedi";
  ownerVatNumber = "10949100159";
  ownerLegalAddressCountry = "IT";
  ownerLegalAddressZip = "20059";
  ownerLegalAddressCity = "Vimercate";
  ownerLegalAddressLine1 = "Piazza Giovanni Paolo II 7";
  ownerPublicEmail = "info@archedi.it";
  ownerWebsite = "https://archedi.com";
  ownerPhone = null;
  ownerEmailNoReply = "noreply@archedi.it";

  defaultLang = "it";

  // Openapi
  openApiUsername = environment.openApiUsername;
  openAPiPassword = environment.openApiPassword;

  // Creditsafe
  creditsafeUsername = environment.creditsafeUsername;
  creditsafePassword = environment.creditsafePassword;

  // Google
  googleMapsApi = environment.googleMapsApi;

  // apiUrl
  // ----
  apiPort = environment.apiPort === "" || environment.apiHost === "80" ? "" : `:${environment.apiPort}`;
  apiHost = environment.apiProtocol + environment.apiHost + this.apiPort;

  frontEndPort = environment.frontEndPort === "" || environment.frontEndPort === "80" ? "" : `:${environment.frontEndPort}`;
  frontEndHost = environment.frontEndProtocol + environment.frontEndHost + this.frontEndPort;

  apiSuppliersPort = environment.suppliersApiPort === "" ? "" : `:${environment.suppliersApiPort}`;
  apiSuppliersHost = environment.suppliersApiProtocol + environment.suppliersApiHost + this.apiSuppliersPort;
  // ----

  // ----
  // apiHost = 'http://localhost:8083' // @todo verificare perchè environment non va (quando è richiamato da attachments-type)
  // apiHost =
  //   environment.apiProtocol +
  //   environment.apiHost +
  //   (environment.apiPort === "" || environment.apiHost === "80")
  //     ? ""
  //     : environment.apiPort;

  // frontEndHost =
  //   environment.frontEndProtocol +
  //     environment.frontEndHost +
  //     (environment.frontEndPort === "" || environment.frontEndPort === "80")
  //     ? ""
  //     : environment.frontEndPort;
  // ----

  // General config
  redirectDelayAfterCreation = 1000;
  debounceTime = 950; // ms

  // Supported modules
  availableModules = [
    {
      value: "users",
      label: this.translateService.instant("sidenav.users")
    },
    {
      value: "permissions",
      label: this.translateService.instant("sidenav.permissions")
    },
    {
      value: "roles",
      label: this.translateService.instant("sidenav.roles")
    },
    {
      value: "companies",
      label: this.translateService.instant("sidenav.companies")
    },
    {
      value: "properties",
      label: this.translateService.instant("sidenav.properties")
    },
    {
      value: "suppliers",
      label: this.translateService.instant("sidenav.suppliers")
    },
    {
      value: "services",
      label: this.translateService.instant("sidenav.services")
    },
    {
      value: "prices",
      label: this.translateService.instant("sidenav.prices")
    },
    {
      value: "subscriptions",
      label: this.translateService.instant("sidenav.subscriptions")
    },
    {
      value: "communications",
      label: this.translateService.instant("sidenav.communications")
    },
    {
      value: "balances",
      label: this.translateService.instant("sidenav.balances")
    },
    {
      value: "stats",
      label: this.translateService.instant("sidenav.stats")
    }
  ];

  // Supported property types
  availablePropertyTypes = ["co", "bsc", "pur", "puc", "sp"];

  // Supported role types
  availableRoleTypes = [
    {
      value: "admin",
      label: this.translateService.instant("roles.types.admin")
    },
    {
      value: "operator",
      label: this.translateService.instant("roles.types.operator")
    },
    {
      value: "master",
      label: this.translateService.instant("roles.types.master")
    },
    {
      value: "collaborator",
      label: this.translateService.instant("roles.types.collaborator")
    },
    {
      value: "temporary",
      label: this.translateService.instant("roles.types.temporary")
    },
    {
      value: "shared",
      label: this.translateService.instant("roles.types.shared")
    },
    {
      value: "external",
      label: this.translateService.instant("roles.types.external")
    },
    {
      value: "tagger",
      label: this.translateService.instant("roles.types.tagger")
    }
  ];

  //
  // START - SUBSCRIPTIONS
  //
  // Available subscriptions codes
  availableSubscriptionCodes = [
    // ABBONAMENTO ANNUALE
    {
      value: "ABB010",
      label: "ABB010 - GESTIONE ANNUALE + GESTIONE RAS",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      // eslint-disable-next-line max-len
      description: `Canone annuale piattaforma archedi per la gestione del "R.A.S. -Registro Anagrafe Sicurezza" e relativo archivio documentale per il periodo dal #startDate# al #endDate#.
      
      Riferimento immobile`,
    },
    {
      value: "ABB011",
      label: "ABB011 - GESTIONE ANNUALE + GESTIONE RAS",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      // eslint-disable-next-line max-len
      description: `Canone annuale piattaforma archedi per la gestione del "R.A.S. -Registro Anagrafe Sicurezza" e relativo archivio documentale, compreso servizio di aggiornamento della piattaforma con inserimento documenti digitali illimitato, per il periodo dal #startDate# al #endDate#.
      
      Riferimento immobile`,
    },
    {
      value: "ABBDINARDI",
      label: "ABBDINARDI - GESTIONE ANNUALE + FASCICOLO IMPIANTI E MANUFATTI",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      // eslint-disable-next-line max-len
      description: `Abbonamento annuale piattaforma archedi per la gestione "Fascicolo Impianti e Manufatti" e relativo archivio documentale per il periodo dal #startDate# al #endDate# completo di aggiornamento anagrafica condominiale per implementazione archivio telematico come concordato.
      
      Riferimento immobile`,
    },
    {
      value: "ABBFERRARI",
      label: "ABBFERRARI - GESTIONE ANNUALE",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      // eslint-disable-next-line max-len
      description: `Fascicolo impianti e manufatti del condominio e relativo archivio documentale, ai sensi della vigente normativa.
      
      Mappatura generale dell'immobile e degli impianti esistenti e inserimento nel software della piattaforma.
      
      Attribuzione ai documenti di un QR code ai fini della loro pronta emissione.`,
    },
    {
      value: "ABB001",
      label: "ABB001 - ABBONAMENTO ANNUALE",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      description: `Abbonamento annuale piattaforma archedi per la gestione "Fascicolo Impianti e Manufatti" e relativo archivio documentale per il periodo dal #startDate# al #endDate# come concordato. 
      
      Riferimento immobile`
    },
    {
      value: "ABB002",
      label: "ABB002 - GESTIONE ANNUALE",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      description: `Gestione e aggiornamento del Registro Anagrafe Sicurezza e del Fascicolo manufatti e impianti con relativo archivio documentale per il periodo dal #startDate# al #endDate#  come concordato.
      
      Riferimento immobile`
    },

    // ABBONAMENTO ANNUALE + FORFAIT INSERIMENTI
    {
      value: "ABB003",
      label: "ABB003 - ABBONAMENTO ANNUALE + FORFAIT INSERIMENTI",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: true,
      description: `Abbonamento annuale piattaforma archedi per la gestione "Fascicolo Impianti e Manufatti" e relativo archivio documentale per il periodo dal #startDate# al #endDate# completo di inserimento nel software dei documenti da Voi fornitoci successivamente come concordato.
      
      Riferimento Immobile`
    },
    {
      value: "ABB004",
      label: "ABB004 - GESTIONE ANNUALE + FORFAIT INSERIMENTI",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: true,
      description: `Gestione e aggiornamento del Registro anagrafe Sicurezza e del Fascicolo manufatti e impianti con relativo archivio documentale per il periodo dal #startDate# al #endDate# completo di inserimento nel software dei documenti da Voi fornitoci successivamente come concordato.
      
      Riferimento immobile`
    },

    // ABBONAMENTO ANNUALE + FORFAIT SCANSIONI E INSERIMENTI
    {
      value: "ABB005",
      label: "ABB005 - ABBONAMENTO ANNUALE + FORFAIT SCANSIONI E INSERIMENTI",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      description: `Abbonamento annuale piattaforma archedi per la gestione "Fascicolo Impianti e Manufatti" e relativo archivio documentale per il periodo dal #startDate# al #endDate# completo di scansione e inserimento  nel software dei documenti da Voi fornitoci successivamente come concordato.
      
      Riferimento Immobile`
    },
    {
      value: "ABB006",
      label: "ABB006 - GESTIONE ANNUALE + FORFAIT SCANSIONI E INSERIMENTI",
      keys: ["FULL"],
      flatRate: false,
      quoteConfirmed: false,
      description: `Gestione e aggiornamento del Registro anagrafe Sicurezza e del Fascicolo manufatti e impianti con relativo archivio documentale per il periodo dal #startDate# al #endDate# completo di scansione e inserimento nel software dei documenti da Voi fornitoci successivamente  come concordato.
      
      Riferimento Immobile`
    },
    // ABBONAMENTO FREE
    {
      value: "ABBFREE001",
      label: "ABBFREE001 - ABBONAMENTO ANNUALE",
      keys: ["FREE", "FREEBASIC"],
      flatRate: false,
      quoteConfirmed: false,
      description: `Abbonamento annuale formula Free alla piattaforma Archedi per il periodo dal #startDate# al #endDate#, come concordato.

      Riferimento Immobile`
    },
  ];

  // Available mapping codes
  availableMappingCodes = [
    // MAPPATURA GENERALE
    {
      value: "MAP001",
      label: "MAPP001 - MAPPATURA GENERALE DELL'IMMOBILE",
      keys: ["FULL"],
      quoteConfirmed: false,
      description: `Realizzazione mappatura generale dell'immobile e degli impianti esistenti, verifica dei dati e creazione Registro Anagrafe Sicurezza tramite piattaforma Archedi

      Riferimento Immobile`
    },
  ];

  // Available scans and inserts codes
  availableScanCodes = [
    // SCANSIONI E INSERIMENTI CARTACEI E DIGITALI PRIMO CONTO
    {
      value: "SCANINS001",
      label: "SCANINS001 - SCANSIONI E INSERIMENTI PRIMO CONTO",
      keys: ["FULL", "FREE", "FREEBASIC"],
      quoteConfirmed: false,
      description: `Inserimento nel software archedi di documenti da Voi fornitoci in formato digitale e cartaceo, scansione dei documenti cartacei in formato pdf e archiviazione in n. ______ scatole di cartone come concordato.

      Riferimento Immobile`
    },
  ];

  // Available Fixed Package codes
  availableFixedPackageCodes = [
    // SCANSIONI E INSERIMENTI CARTACEI E DIGITALI PACCHETTO FISSO PRIMO CONTO
    {
      value: "PACC001",
      label: "PACC001 - PACCHETTO FISSO", // PACC001 - SCANSIONI E INSERIMENTI PACCHETTO FISSO
      keys: ["FULL"],
      quoteConfirmed: false,
      description: `Realizzazione mappatura generale dell'immobile e degli impianti esistenti, verifica dei dati e creazione Registro Anagrafe Sicurezza tramite piattaforma Archedi

      Inserimento nel software archedi di documenti da Voi fornitoci in formato digitale e cartaceo compresa la scansione dei documenti cartacei in formato pdf e archiviazione in n. ______ scatole di cartone come concordato`
    },
  ];

  // Available scans and inserts codes next first balance
  availableInsCodesNext = [
    // INSERIMENTI SUCCESSIVI CARTACEI E DIGITALI A LISTINO
    {
      value: "INSSUCC001",
      label: "INSSUCC001 - INSERIMENTI SUCCESSIVI A LISTINO",
      keys: ["FULL", "FREE", "FREEBASIC"],
      quoteConfirmed: false,
      description: `Inserimento nel software archedi di documenti cartacei e digitali da Voi fornitoci successivamente come concordato

      Riferimento Immobile`
    },
  ];

  // Available scans and inserts codes next first balance Forfait
  availableInsCodesNextForfait = [
    // INSERIMENTI SUCCESSIVI CARTACEI E DIGITALI A FORFAIT
    {
      value: "FINSSUCC001",
      label: "FINSSUCC001 - INSERIMENTI SUCCESSIVI A FORFAIT",
      keys: ["FULL", "FREE", "FREEBASIC"],
      quoteConfirmed: false,
      description: `Servizio di aggiornamento della piattaforma con inserimento documenti digitali illimitato per il periodo dal #startDate# al #endDate# come concordato.

      Riferimento Immobile`
    },
  ];

  // Available scans  codes next first balance
  availableScanCodesNext = [
    // 
    {
      value: "SCASUCC001",
      label: "SCASUCC001 - SCANSIONI SUCCESSIVE A LISTINO",
      keys: ["FULL", "FREE", "FREEBASIC"],
      quoteConfirmed: false,
      description: `Scansione di documenti cartacei da Voi fornitoci successivamente come da prezzo concordato

      Riferimento Immobile`
    },
  ];

  // Available scans and inserts codes next first balance Forfait
  availableScanCodesNextForfait = [
    // 
    {
      value: "FSCASUCC001",
      label: "FSCASUCC001 - SCANSIONI SUCCESSIVE A FORFAIT",
      keys: ["FULL", "FREE", "FREEBASIC"],
      quoteConfirmed: false,
      description: `Scansione di documenti cartacei da Voi fornitoci successivamente come da prezzo forfettario concordato

      Riferimento Immobile`
    },
  ];

  // Codes available to be used within the open balance
  availableAccessoriesCodes = [
    // CD CONDOMINI
    {
      value: "CD001",
      label: "CD001",
      keys: ["FULL", "FREE", "FREEBASIC"],
      description: `Servizio di salvataggio dei Vostri documenti presenti nella piattaforma archedi su dispositivo digitale (CD o USB), come concordato`
    },
    // COSTI SUPPLEMENTARI
    {
      value: "SUPPL001",
      label: "SUPPL001",
      keys: ["FULL", "FREE", "FREEBASIC"],
      description: `Costo totale per n. ___ unità abitative o commerciali supplementari`
    },
    {
      value: "SUPPL002",
      label: "SUPPL002",
      keys: ["FULL", "FREE", "FREEBASIC"],
      description: `Costo totale per n. ______ box supplementari`
    },
    // INSERIMENTI SUCCESSIVI CARTACEI E DIGITALI A LISTINO
    {
      value: "INSSUCC001",
      label: "INSSUCC001",
      keys: ["FULL", "FREE", "FREEBASIC"],
      description: `Inserimento nel software archedi di documenti cartacei e digitali da Voi fornitoci successivamente come concordato

      Riferimento Immobile`
    },
    // SCATOLE DI CARTONE
    {
      value: "SCAT001",
      label: "SCAT001",
      keys: ["FULL", "FREE", "FREEBASIC"],
      description: `Fornitura di n. _____ scatole di cartone per archiviazione, come concordato

      Riferimento Immobile`
    },
    // SPAZIO DI ARCHIVIAZIONE SUPPLEMENTARE
    {
      value: "GBSUPP001",
      label: "GBSUPP001",
      keys: ["FULL", "FREE", "FREEBASIC"],
      description: `N. 1 GB per spazio di archiviazione supplementare`
    },
    // CAMPO LIBERO
    {
      value: "LIBE001",
      label: "LIBE001",
      keys: ["FULL", "FREE", "FREEBASIC"],
      description: ``
    },
  ];

  // Options to subscriptions
  availableOptionsCodes = [
    // GESTIONE FORNITORI
    {
      value: "SPL001",
      label: "SPL001",
      keys: ["FULL", "FREE", "FREEBASIC"],
      quoteConfirmed: false,
      description: `Abbonamento anno solare #year# gestione fornitori.

      Riferimento immobile`
    },
  ];

  //
  // END - SUBSCRIPTIONS
  //

  // Supported macro document areas (1st order categories)
  // DO NOT CHANGE ORDER
  availableDocumentAreas = [
    'amministrativo',
    'contratti',
    'tecnico',
    'unitaImmobiliari',
  ];

  // Supported document topics (2nd order categories)
  // DO NOT CHANGE ORDER
  availableDocumentTopics = {
    amministrativo: [
      'gestionaleAmministrativo',
      'amministrativiImmobile',
      'gestionaleContabile',
      'catasto',
      'urbanisticaEdEdilizia',
      'stackingPlan',
      'reportistica',
      'locazione', // oppure 'locazioneContrdiLocazione' ???
    ],
    contratti: [
      'contrattiFornitureServizi' // oppure 'contratti' ???
    ],
    tecnico: [
      'lineaVita',
      'amiantoDoc',
      'edile',
      'impiantoElettrico',
      'riscaldamentoCondizionamento',
      'ascensoriEMontauto',
      'trattamentoAcqua',
      'impiantiElettronici',
      'attivitaSottopostePrevenzioneIncendi',
      'impiantiAntincendio',
      'impiantiSpeciali',
      'impiantiRicreativiSportivi',
      'R6manutenzioni',
    ],
    unitaImmobiliari: [
      'unitaImmobiliari',
    ]
  };

  // Supported country codes
  availableCountryCodes = countriesData;

  // Supported balance status
  availableBalanceStatus = ["open", "pending", "closed"];
  availableDdtStatus = ["open", "closed"];
  availableInterventionStatus = ["open", "closed"];

  // Supported balance status
  availableSupplierDocuments = [
    "vc",
    "durc",
    "dvr",
    "rspp",
    "ris",
    "pos",
  ];

  // Supported balance status
  availablePrices = [
    "box",
    "newDigitalDocFirstFile",
    "newDigitalDocOtherFile",
    "newPaperDocFirstFile",
    "newPaperDocOtherFile",
    "scanA0",
    "scanA1",
    "scanA2",
    "scanA3",
    "scanA4",
    "scanNoFormat",
  ];

  // Structs to display in stats page
  structuresToDisplay = [
    "impiantiElettrici",
    "trattamentoAcqua",
    "riscaldamentoCondizionamento",
    "dispositiviAntincendio",
    "ascensoriEMontauto",
    "impiantiElettronici",
    "lineeVita",
    "amianto",
    "impiantiSpeciali",
    "impiantiRicreativi",
    "additionalData",
  ];

  allowedDocumentExtensions = 'pdf,jpg,jpeg,png,gif,dwg,msg,p7m,cab,dxf,dwf,avi,mp4,mpg,mpeg,mkv';

  maximumNumberOfAttachmentsPerEmail = 80;

  // Pattern for email validation
  // tslint:disable-next-line:max-line-length
  readonly passwordPattern: string = new RegExp(/^(\w+\d+)$/).source;
  // Pattern for email validation
  // tslint:disable-next-line:max-line-length
  readonly emailPattern: string = new RegExp(
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
  ).source;

  // Pattern for phone number validation
  // tslint:disable-next-line:max-line-length
  readonly phonePattern: string = new RegExp(
    /^((((\d){2,4}|((\d){3}-(\d){1}))(\s)?(\d){6,7})|([+39]{3}(\s)?((\d){2,4}|((\d){3}-(\d){1}))(\d){6,7})|([+39]{3}(\s)?((\d){2,4}|((\d){3}-(\d){1}))(\s)?(\d){6,7}))$/,
    "m"
  ).source;

  // Pattern for webSiteUrl validation
  // tslint:disable-next-line:max-line-length
  readonly webSitePattern: string = new RegExp(
    /^(https?:\/\/)?(\w+\.)[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/
  ).source;

  // Date formats
  timeFormat = "HH:mm:ss";
  dateFormat = "dd/MM/yyyy";
  dateTimeFormat = "dd/MM/yyyy HH:mm:ss";

  // Companies exception
  companiesException = [
    '6200ea6b4b7f605b6f132e50', // LA RETE - SOCIETA' COOPERATIVA SOCIALE - ONLUS
  ]

  /**
   * Class constructor
   */
  constructor(private translateService: TranslateService) { }
}
